import React, { useEffect } from "react";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Pagination from "@cloudscape-design/components/pagination";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { createDepositLadderStatisticsFilters, depositLadderStatisticsStore, depositLadderStatisticsUsersStore } from "../stores/depositLadder";

const MODE_OPTIONS = [
	{ label: "Any mode", value: "" },
	{ label: "All", value: "all" },
	{ label: "Affiliate", value: "affiliate" },
];

const STATUS_OPTIONS = [
	{ label: "Any status", value: "" },
	{ label: "Active", value: "active" },
	{ label: "Finished", value: "finished" },
];

const ALL_LADDER_OPTION = { label: "All deposit ladders", value: "" };

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Set a custom range in the past",
	customRelativeRangeUnitLabel: "Unit",
	formatRelativeRange: (value) => value,
	formatUnit: (unit, value) => `${value} ${unit}`,
	dateTimeConstraintText: "Use YYYY/MM/DD.",
	relativeModeTitle: "Relative range",
	absoluteModeTitle: "Absolute range",
	relativeRangeSelectionHeading: "Choose a range",
	startDateLabel: "Start date",
	endDateLabel: "End date",
	startTimeLabel: "Start time",
	endTimeLabel: "End time",
	clearButtonLabel: "Clear",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
};

const getOption = (options, value) => options.find((option) => option.value === value) || options[0];

const formatNumber = (value) => {
	const number = Number(value || 0);

	return Number.isFinite(number) ? number.toLocaleString() : "0";
};

const formatMoney = (value) => `${formatNumber(Number(value || 0).toFixed(2))} RUB`;

const formatPercent = (value) => `${formatNumber(Number(value || 0).toFixed(2))}%`;

const formatDateTime = (value) => {
	const date = new Date(value);

	return value && !Number.isNaN(date.getTime()) ? `${date.toLocaleDateString()} ${date.toLocaleTimeString()}` : "-";
};

function MetricCard({ label, value }) {
	return (
		<Container>
			<SpaceBetween size="xs">
				<Box variant="awsui-key-label">{label}</Box>
				<Box variant="h2">{value}</Box>
			</SpaceBetween>
		</Container>
	);
}

function DepositLadderAnalytics(props) {
	const statistics = useRecoilValue(depositLadderStatisticsStore);
	const users = useRecoilValue(depositLadderStatisticsUsersStore);
	const setStatistics = useSetRecoilState(depositLadderStatisticsStore);
	const setUsers = useSetRecoilState(depositLadderStatisticsUsersStore);
	const filters = statistics.filters;
	const summary = statistics.data?.summary || {};

	const updateFilter = (field, value) => setStatistics({ ...statistics, filters: { ...filters, [field]: value } });

	const updateMode = (value) =>
		setStatistics({
			...statistics,
			filters: {
				...filters,
				mode: value,
				refererUid: value === "affiliate" ? filters.refererUid : "",
			},
		});

	const loadData = () => {
		props.Interface.loadDepositLadderStatistics();
		props.Interface.loadDepositLadderStatisticsUsers();
	};

	const applyFilters = () => {
		setUsers({ ...users, pagination: { ...users.pagination, current: 1 } });
		setTimeout(loadData, 0);
	};

	const clearFilters = () => {
		setStatistics({ ...statistics, filters: createDepositLadderStatisticsFilters() });
		setUsers({ ...users, pagination: { ...users.pagination, current: 1 } });
		props.Interface.loadDepositLadderStatisticsOptions();
		setTimeout(loadData, 0);
	};

	useEffect(() => {
		props.Interface.loadDepositLadderStatisticsOptions();
		loadData();
	}, []);

	return (
		<SpaceBetween size="l">
			<Container header={<Header variant="h2">Filters</Header>}>
				<SpaceBetween size="m" direction="vertical">
					<SpaceBetween size="m" direction="horizontal">
						<FormField label="Assigned date" description="Filters users by ladder assignment date.">
							<DateRangePicker value={filters.date} onChange={({ detail }) => updateFilter("date", detail.value)} i18nStrings={DATE_RANGE_I18N} placeholder="Select assignment date" dateOnly isValidRange={() => ({ valid: true })} />
						</FormField>
						<FormField label="Deposit ladder" description="Shows first 100 ladders, or search by exact ID / name.">
							<Select
								filteringType="manual"
								selectedOption={filters.selectedLadder || ALL_LADDER_OPTION}
								options={[ALL_LADDER_OPTION, ...statistics.ladderOptions]}
								onLoadItems={({ detail }) => props.Interface.loadDepositLadderStatisticsOptions(detail.filteringText)}
								onChange={({ detail }) => updateFilter("selectedLadder", detail.selectedOption.value ? detail.selectedOption : null)}
								placeholder="Search deposit ladder"
								statusType={statistics.ladderOptionsLoading ? "loading" : "finished"}
							/>
						</FormField>
						<FormField label="Mode" description="Filters assignments by ladder mode.">
							<Select selectedOption={getOption(MODE_OPTIONS, filters.mode)} options={MODE_OPTIONS} onChange={({ detail }) => updateMode(detail.selectedOption.value)} />
						</FormField>
					</SpaceBetween>
					<SpaceBetween direction="horizontal" size="m">
						{filters.mode === "affiliate" && (
							<FormField label="Affiliate referer UIDs" description="Only for Affiliate mode. Use comma or space separated IDs.">
								<Input value={filters.refererUid} onChange={({ detail }) => updateFilter("refererUid", detail.value)} placeholder="Example: 123, 456 789" />
							</FormField>
						)}
						<FormField label="User UIDs" description="Use comma or space separated player IDs.">
							<Input value={filters.uid} onChange={({ detail }) => updateFilter("uid", detail.value)} placeholder="Example: 123, 456 789" />
						</FormField>
						<FormField label="Assignment status" description="Active means the user still has available or pending ladder steps.">
							<Select selectedOption={getOption(STATUS_OPTIONS, filters.status)} options={STATUS_OPTIONS} onChange={({ detail }) => updateFilter("status", detail.selectedOption.value)} />
						</FormField>
					</SpaceBetween>
					{/* <Grid gridDefinition={filters.mode === "affiliate" ? [{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }] : [{ colspan: 6 }, { colspan: 6 }]}></Grid> */}
					<Grid gridDefinition={[{ colspan: 12 }]}>
						<Box>
							<SpaceBetween direction="horizontal" size="s">
								<Button variant="primary" onClick={applyFilters}>
									Apply
								</Button>
								<Button onClick={clearFilters}>Clear</Button>
							</SpaceBetween>
						</Box>
					</Grid>
				</SpaceBetween>
			</Container>

			<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
				<MetricCard label="Assigned users" value={formatNumber(summary.assignedUsers)} />
				<MetricCard label="Active assignments" value={formatNumber(summary.activeAssignments)} />
				<MetricCard label="Finished ladders" value={formatNumber(summary.finishedLadders)} />
				<MetricCard label="Successful deposits" value={formatNumber(summary.successfulDeposits)} />
				<MetricCard label="Deposit amount" value={formatMoney(summary.depositAmount)} />
				<MetricCard label="Average deposit" value={formatMoney(summary.averageDepositAmount)} />
			</Grid>
			<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
				<MetricCard label="Completed steps" value={formatNumber(summary.completedSteps)} />
				<MetricCard label="Claimed steps" value={formatNumber(summary.claimedSteps)} />
				<MetricCard label="Expired steps" value={formatNumber(summary.expiredSteps)} />
				<MetricCard label="Not eligible steps" value={formatNumber(summary.notEligibleSteps)} />
			</Grid>
			<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
				<MetricCard label="Credited bonus" value={formatMoney(summary.creditedBonusAmount)} />
				<MetricCard label="Claim conversion" value={formatPercent(summary.claimConversion)} />
				<MetricCard label="Bonus cost ratio" value={formatPercent(summary.bonusCostRatio)} />
			</Grid>

			<Table
				loading={!statistics.loaded}
				items={statistics.data?.byLadder || []}
				columnDefinitions={[
					{ id: "ladderId", header: "Ladder ID", cell: (item) => item.ladderId },
					{ id: "name", header: "Name", cell: (item) => item.name },
					{ id: "mode", header: "Mode", cell: (item) => item.mode },
					{ id: "assignedUsers", header: "Assigned users", cell: (item) => formatNumber(item.assignedUsers) },
					{ id: "completed", header: "Completed", cell: (item) => formatNumber(item.completed) },
					{ id: "claimed", header: "Claimed", cell: (item) => formatNumber(item.claimed) },
					{ id: "expired", header: "Expired", cell: (item) => formatNumber(item.expired) },
					{ id: "notEligible", header: "Not eligible", cell: (item) => formatNumber(item.not_eligible) },
					{ id: "depositAmount", header: "Deposit amount", cell: (item) => formatMoney(item.depositAmount) },
					{ id: "creditedBonusAmount", header: "Credited bonus", cell: (item) => formatMoney(item.creditedBonusAmount) },
					{ id: "claimConversion", header: "Claim conversion", cell: (item) => formatPercent(item.claimConversion) },
					{ id: "costRatio", header: "Cost ratio", cell: (item) => formatPercent(item.costRatio) },
				]}
				header={<Header>Breakdown by ladder</Header>}
				empty="No ladder statistics found"
			/>

			<Table
				loading={!statistics.loaded}
				items={statistics.data?.byStep || []}
				columnDefinitions={[
					{ id: "ladder", header: "Ladder", cell: (item) => `${item.name} (#${item.ladderId})` },
					{ id: "depositNumber", header: "Deposit", cell: (item) => item.depositNumber },
					{ id: "bonusPercent", header: "Bonus %", cell: (item) => formatPercent(item.bonusPercent) },
					{ id: "pending", header: "Pending", cell: (item) => formatNumber(item.pending) },
					{ id: "completed", header: "Completed", cell: (item) => formatNumber(item.completed) },
					{ id: "claimed", header: "Claimed", cell: (item) => formatNumber(item.claimed) },
					{ id: "expired", header: "Expired", cell: (item) => formatNumber(item.expired) },
					{ id: "notEligible", header: "Not eligible", cell: (item) => formatNumber(item.not_eligible) },
					{ id: "depositCount", header: "Deposits", cell: (item) => formatNumber(item.depositCount) },
					{ id: "depositAmount", header: "Deposit sum", cell: (item) => formatMoney(item.depositAmount) },
					{ id: "averageDepositAmount", header: "Avg deposit", cell: (item) => formatMoney(item.averageDepositAmount) },
					{ id: "creditedBonusAmount", header: "Credited bonus", cell: (item) => formatMoney(item.creditedBonusAmount) },
					{ id: "assignedAfterDeposit", header: "Assigned after deposit", cell: (item) => formatNumber(item.assignedAfterDeposit) },
					{ id: "minDepositNotReached", header: "Min deposit not reached", cell: (item) => formatNumber(item.minDepositNotReached) },
					{ id: "claimPeriodExpired", header: "Claim period expired", cell: (item) => formatNumber(item.claimPeriodExpired) },
				]}
				header={<Header>Breakdown by step</Header>}
				empty="No step statistics found"
			/>

			<Table
				loading={!users.loaded}
				items={users.data}
				columnDefinitions={[
					{ id: "uid", header: "UID", cell: (item) => item.uid },
					{ id: "ladder", header: "Ladder", cell: (item) => `${item.ladderName} (#${item.ladderId})` },
					{ id: "mode", header: "Mode", cell: (item) => item.mode },
					{ id: "refererUid", header: "Referer UID", cell: (item) => item.refererUid || "-" },
					{ id: "assignedAt", header: "Assigned at", cell: (item) => formatDateTime(item.assignedAt) },
					{ id: "currentStatus", header: "Current status", cell: (item) => item.currentStatus },
					{ id: "currentStep", header: "Current step", cell: (item) => item.currentStep || "-" },
					{ id: "depositsCount", header: "Deposits", cell: (item) => formatNumber(item.depositsCount) },
					{ id: "depositAmount", header: "Deposit sum", cell: (item) => formatMoney(item.depositAmount) },
					{ id: "claimedSteps", header: "Claimed steps", cell: (item) => item.claimedSteps?.join(", ") || "-" },
					{ id: "creditedAmount", header: "Credited bonus", cell: (item) => formatMoney(item.creditedAmount) },
					{ id: "expiredSteps", header: "Expired steps", cell: (item) => formatNumber(item.expiredSteps) },
					{ id: "notEligibleSteps", header: "Not eligible steps", cell: (item) => formatNumber(item.notEligibleSteps) },
				]}
				header={<Header>Users</Header>}
				pagination={
					<Pagination
						currentPageIndex={users.pagination.current}
						pagesCount={users.pagination.total}
						onChange={({ detail }) => {
							setUsers({ ...users, pagination: { ...users.pagination, current: detail.currentPageIndex } });
							setTimeout(() => props.Interface.loadDepositLadderStatisticsUsers(), 0);
						}}
					/>
				}
				empty="No users found"
			/>
		</SpaceBetween>
	);
}

export default DepositLadderAnalytics;
